import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  Compass,
  CheckCircle,
  Buildings,
  Users,
  CurrencyCircleDollar,
  Gear,
  Target,
  ChartLineUp,
  ShieldCheck,
  Wallet,
  Receipt,
  Gift,
  CreditCard,
  Heartbeat,
  Lightning,
  Warning,
  Plus,
  Sparkle,
  TrendUp,
  SealCheck,
  ShieldPlus,
  Table,
  CircleNotch,
  WarningCircle,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Layout } from "../components/layout/Layout";
import { API_URL } from "../lib/utils";
import { LiquidGlassBg } from "../components/ui/LiquidGlassComponents";

const TOTAL_STEPS = 9;

const SPONSORSHIP_OPTIONS = [
  {
    value: "yes",
    label: "Direct Sponsorship",
    sub: "We want to sponsor employee healthcare",
    icon: ShieldPlus,
  },
  {
    value: "no",
    label: "Zero-Budget Access",
    sub: "Our budget currently does not allow sponsorship",
    icon: WarningCircle,
  },
];

const EMPLOYEE_BANDS = [
  { value: "1_25", label: "1 to 25" },
  { value: "26_50", label: "26 to 50" },
  { value: "51_100", label: "51 to 100" },
  { value: "101_250", label: "101 to 250" },
  { value: "251_500", label: "251 to 500" },
  { value: "501_1000", label: "501 to 1,000" },
  { value: "1001_5000", label: "1,001 to 5,000" },
  { value: "5001_10000", label: "5,001 to 10,000" },
  { value: "10000_plus", label: "10,000+" },
];

const BUDGET_BANDS = [
  { value: "no_formal_budget", label: "No formal budget" },
  { value: "100k_500k", label: "100k to 500k" },
  { value: "500k_1m", label: "500k to 1M" },
  { value: "1m_2.5m", label: "1M to 2.5M" },
  { value: "2.5m_5m", label: "2.5M to 5M" },
  { value: "5m_10m", label: "5M to 10M" },
  { value: "10m_25m", label: "10M to 25M" },
  { value: "25m_50m", label: "25M to 50M" },
  { value: "50m_100m", label: "50M to 100M" },
  { value: "above_100m", label: "Above 100M" },
];

const CURRENT_MODELS = [
  {
    value: "no_formal",
    label: "No formal healthcare benefit",
    icon: CircleNotch,
  },
  { value: "manual_reimbursement", label: "Manual reimbursement", icon: Table },
  { value: "insurance_only", label: "Insurance only", icon: ShieldCheck },
  {
    value: "self_funded_pool",
    label: "Self-funded or pool-based",
    icon: Wallet,
  },
  { value: "mixed_fragmented", label: "Mixed or fragmented model", icon: Gear },
];

const PAIN_POINTS = [
  { value: "rising_premiums", label: "Rising insurance premiums" },
  { value: "no_visibility", label: "No cost visibility" },
  { value: "admin_burden", label: "Too much HR/admin work" },
  { value: "delayed_reimbursements", label: "Delayed reimbursements" },
  { value: "claim_leakage", label: "Claim leakage or fraud concerns" },
  {
    value: "employees_pay_out_of_pocket",
    label: "High employee out-of-pocket",
  },
  { value: "no_budget", label: "Budget constraints" },
  { value: "liquidity_control", label: "Need liquidity control" },
  { value: "disconnected_vendors", label: "Disconnected vendors" },
  { value: "no_preventive_care", label: "Lacking preventive care" },
  { value: "need_forecasting", label: "Need forecasting & governance" },
];

const VALUE_GOALS = [
  { value: "hospitalization", label: "Hospitalization coverage" },
  { value: "opd_pharmacy", label: "OPD & pharmacy support" },
  { value: "structured_reimbursement", label: "Structured reimbursement" },
  { value: "cashless_access", label: "Cashless access" },
  { value: "preventive_wellness", label: "Preventive wellness" },
  { value: "cost_control", label: "Better cost control" },
  { value: "liquidity_flexibility", label: "Liquidity & flexibility" },
  { value: "transparent_insurance", label: "Transparent selection" },
  { value: "scalable_model", label: "Scalable infrastructure" },
];

const FINANCIAL_PREFERENCES = [
  { value: "fixed_premium", label: "Fixed annual premium" },
  { value: "flexible_funding", label: "Flexible funding/top-up" },
  { value: "pay_per_use", label: "Reimburse only on usage" },
  { value: "no_funding", label: "No employer funding" },
  { value: "need_credit", label: "Need healthcare credit" },
];

const CONTROL_PREFERENCES = [
  {
    value: "mentor_managed",
    label: "Mentor Managed",
    sub: "We want Mentor to handle full administration",
  },
  {
    value: "shared_control",
    label: "Shared Control",
    sub: "Collaborative management with Mentor support",
  },
  {
    value: "full_internal",
    label: "Internal Control",
    sub: "Full internal control through digital tools",
  },
];

const GROWTH_OUTLOOKS = [
  { value: "stable", label: "Stable Operations" },
  { value: "moderate_growth", label: "Moderate Growth" },
  { value: "fast_growth", label: "Aggressive Expansion" },
  { value: "multi_city", label: "Multi-City Expansion" },
  { value: "scalable_multi_model", label: "Hybrid Scalability" },
];

const RAIL_ICONS = {
  "Insurance Rail": ShieldCheck,
  "Wallet-Based Pool Management": Wallet,
  "Reimbursement Rail": Receipt,
  "Zero Budget Corporate Access": Gift,
  "Employer Backed Health Credit": CreditCard,
  "Wellness and Preventive Care": Heartbeat,
  "Integrated Multi-Rail Infrastructure": Gear,
  "Employee Wallet": Wallet,
  "Employee Wallet for OPD": Wallet,
};

export default function DesignBenefitPlan() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    sponsorship_intent: "",
    employee_band: "",
    annual_budget_band: "",
    annual_budget_actual: "",
    current_model: "",
    pain_points: [],
    value_goals: [],
    financial_preference: "",
    control_preference: "",
    growth_outlook: "",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Format PKR Helper
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-PK", {
      style: "currency",
      currency: "PKR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleArrayField = (field, value) => {
    setFormData((prev) => {
      const arr = prev[field];
      if (arr.includes(value)) {
        return { ...prev, [field]: arr.filter((v) => v !== value) };
      }
      return { ...prev, [field]: [...arr, value] };
    });
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return !!formData.sponsorship_intent;
      case 2:
        return !!formData.employee_band;
      case 3:
        return (
          !!formData.annual_budget_band &&
          (formData.annual_budget_band !== "above_100m" ||
            formData.annual_budget_actual)
        );
      case 4:
        return !!formData.current_model;
      case 5:
        return formData.pain_points.length > 0;
      case 6:
        return formData.value_goals.length > 0;
      case 7:
        return !!formData.financial_preference;
      case 8:
        return !!formData.control_preference;
      case 9:
        return !!formData.growth_outlook;
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_URL}/calculators/benefit-plan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          annual_budget_actual: formData.annual_budget_actual
            ? parseFloat(formData.annual_budget_actual)
            : null,
        }),
      });

      if (!response.ok) throw new Error("Failed to generate recommendation");
      const data = await response.json();
      setResult(data);
      setStep(10);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (step === 9) {
      handleSubmit();
    } else {
      setStep(step + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const renderProgressBar = () => (
    <div className="mb-12">
      <div className="flex justify-between items-end mb-4">
        <div className="space-y-1">
          <p className="text-[10px] font-black text-[#0048ff] uppercase tracking-[0.2em]">
            Strategy Phase
          </p>
          <h3 className="text-xl font-black text-white">
            0{Math.min(step, 9)} <span className="text-white/20">/</span> 09
          </h3>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-black text-white/30 uppercase tracking-[0.2em] mb-1">
            Configuration Progress
          </p>
          <p className="text-sm font-bold text-[#0048ff]">
            {Math.round((Math.min(step, 9) / TOTAL_STEPS) * 100)}%
          </p>
        </div>
      </div>
      <div className="h-1 bg-white/5 rounded-full overflow-hidden">
        <motion.div
          className="h-full bg-gradient-to-r from-[#0048ff] to-[#00d2ff]"
          initial={{ width: 0 }}
          animate={{ width: `${(Math.min(step, 9) / TOTAL_STEPS) * 100}%` }}
          transition={{ duration: 0.5, ease: "circOut" }}
        />
      </div>
    </div>
  );

  const renderOptionCard = (option, selected, onClick, showIcon = false) => (
    <motion.button
      key={option.value}
      type="button"
      onClick={onClick}
      className={`w-full p-6 rounded-2xl border transition-all text-left relative group overflow-hidden ${
        selected
          ? "border-[#0048ff] bg-[#0048ff]/5 shadow-[0_0_30px_rgba(0,72,255,0.1)]"
          : "border-white/5 bg-white/5 hover:bg-white/[0.08] hover:border-white/20"
      }`}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
    >
      <div className="flex items-center gap-4 relative z-10">
        {showIcon && option.icon && (
          <div
            className={`p-3 rounded-xl ${selected ? "bg-[#0048ff]/20 text-[#0048ff]" : "bg-white/5 text-white/40"}`}
          >
            <option.icon size={24} weight="duotone" />
          </div>
        )}
        <div className="flex-1">
          <p
            className={`font-bold ${selected ? "text-[#0048ff]" : "text-white"}`}
          >
            {option.label}
          </p>
          {option.sub && (
            <p className="text-xs text-white/40 mt-1">{option.sub}</p>
          )}
        </div>
        <div
          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
            selected ? "border-[#0048ff] bg-[#0048ff]" : "border-white/10"
          }`}
        >
          {selected && (
            <CheckCircle size={16} className="text-white" weight="bold" />
          )}
        </div>
      </div>
      {selected && (
        <motion.div
          layoutId="active-bg"
          className="absolute inset-0 bg-gradient-to-br from-[#0048ff]/10 to-transparent pointer-events-none"
        />
      )}
    </motion.button>
  );

  const renderMultiSelectCard = (option, selected, onClick) => (
    <motion.button
      key={option.value}
      type="button"
      onClick={onClick}
      className={`p-5 rounded-2xl border transition-all text-left relative group ${
        selected
          ? "border-[#0048ff] bg-[#0048ff]/5"
          : "border-white/5 bg-white/5 hover:bg-white/[0.08]"
      }`}
      whileHover={{ y: -2 }}
    >
      <div className="flex items-center gap-3 relative z-10">
        <div
          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
            selected ? "border-[#0048ff] bg-[#0048ff]" : "border-white/20"
          }`}
        >
          {selected && (
            <CheckCircle size={14} className="text-white" weight="bold" />
          )}
        </div>
        <span
          className={`text-sm font-medium ${selected ? "text-white" : "text-white/60"}`}
        >
          {option.label}
        </span>
      </div>
    </motion.button>
  );

  const renderStepHeader = (icon, title, sub) => (
    <div className="text-center mb-10">
      <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-6 group-hover:border-[#0048ff]/50 transition-all">
        {React.createElement(icon, {
          size: 32,
          className: "text-[#0048ff]",
          weight: "duotone",
        })}
      </div>
      <h2 className="text-3xl font-black text-white mb-3 tracking-tight">
        {title}
      </h2>
      <p className="text-white/40 max-w-sm mx-auto leading-relaxed">{sub}</p>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            {renderStepHeader(
              Buildings,
              "Sponsorship Intent",
              "Define your organizational approach towards sponsoring employee healthcare benefits.",
            )}
            <div className="space-y-3">
              {SPONSORSHIP_OPTIONS.map((opt) =>
                renderOptionCard(
                  opt,
                  formData.sponsorship_intent === opt.value,
                  () => updateField("sponsorship_intent", opt.value),
                  true,
                ),
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            {renderStepHeader(
              Users,
              "Workforce Scale",
              "Identify the total number of employee lives to be integrated into the healthcare architecture.",
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {EMPLOYEE_BANDS.map((opt) =>
                renderOptionCard(
                  opt,
                  formData.employee_band === opt.value,
                  () => updateField("employee_band", opt.value),
                ),
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            {renderStepHeader(
              CurrencyCircleDollar,
              "Fiscal Allocation",
              "Specify the annual capital allocation intended for infrastructure and premium support.",
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {BUDGET_BANDS.map((opt) =>
                renderOptionCard(
                  opt,
                  formData.annual_budget_band === opt.value,
                  () => updateField("annual_budget_band", opt.value),
                ),
              )}
            </div>
            {formData.annual_budget_band === "above_100m" && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-8 space-y-4"
              >
                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <Label className="text-[10px] font-black text-white/30 uppercase tracking-widest mb-2 block">
                    Precise Annual Expenditure (PKR)
                  </Label>
                  <Input
                    type="number"
                    placeholder="e.g., 150000000"
                    value={formData.annual_budget_actual}
                    onChange={(e) =>
                      updateField("annual_budget_actual", e.target.value)
                    }
                    className="bg-transparent border-none text-xl font-bold text-white focus-visible:ring-0 p-0 h-auto placeholder:text-white/10"
                  />
                </div>
              </motion.div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            {renderStepHeader(
              Gear,
              "Current Architecture",
              "Map your existing legacy healthcare model to identify optimization opportunities.",
            )}
            <div className="space-y-3">
              {CURRENT_MODELS.map((opt) =>
                renderOptionCard(
                  opt,
                  formData.current_model === opt.value,
                  () => updateField("current_model", opt.value),
                  true,
                ),
              )}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            {renderStepHeader(
              WarningCircle,
              "Governance Bottlenecks",
              "Identify the critical pain points currently affecting your healthcare operations and employee satisfaction.",
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {PAIN_POINTS.map((opt) =>
                renderMultiSelectCard(
                  opt,
                  formData.pain_points.includes(opt.value),
                  () => toggleArrayField("pain_points", opt.value),
                ),
              )}
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            {renderStepHeader(
              Target,
              "Strategic Value Goals",
              "Select the key objectives and value outcomes you want to achieve through this infrastructure.",
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {VALUE_GOALS.map((opt) =>
                renderMultiSelectCard(
                  opt,
                  formData.value_goals.includes(opt.value),
                  () => toggleArrayField("value_goals", opt.value),
                ),
              )}
            </div>
          </div>
        );

      case 7:
        return (
          <div className="space-y-6">
            {renderStepHeader(
              CurrencyCircleDollar,
              "Financial Protocol",
              "Define the preferred financial settlement and funding structure for the plan.",
            )}
            <div className="space-y-3">
              {FINANCIAL_PREFERENCES.map((opt) =>
                renderOptionCard(
                  opt,
                  formData.financial_preference === opt.value,
                  () => updateField("financial_preference", opt.value),
                ),
              )}
            </div>
          </div>
        );

      case 8:
        return (
          <div className="space-y-6">
            {renderStepHeader(
              Gear,
              "Operational Control",
              "Determine the level of operational autonomy and management oversight required.",
            )}
            <div className="space-y-3">
              {CONTROL_PREFERENCES.map((opt) =>
                renderOptionCard(
                  opt,
                  formData.control_preference === opt.value,
                  () => updateField("control_preference", opt.value),
                ),
              )}
            </div>
          </div>
        );

      case 9:
        return (
          <div className="space-y-6">
            {renderStepHeader(
              ChartLineUp,
              "Growth Projection",
              "Quantify the expected workforce evolution to ensure multi-year architectural scalability.",
            )}
            <div className="space-y-3">
              {GROWTH_OUTLOOKS.map((opt) =>
                renderOptionCard(
                  opt,
                  formData.growth_outlook === opt.value,
                  () => updateField("growth_outlook", opt.value),
                ),
              )}
            </div>
          </div>
        );

      case 10:
        return renderResults();

      default:
        return null;
    }
  };

  const renderResults = () => {
    if (!result) return null;

    const PrimaryIcon = RAIL_ICONS[result.primary_plan] || ShieldCheck;
    const SecondaryIcon = result.secondary_plan
      ? RAIL_ICONS[result.secondary_plan.split(" + ")[0]] || Wallet
      : null;
    const OptionalIcon = result.optional_plan
      ? RAIL_ICONS[result.optional_plan.split(" + ")[0]] || Heartbeat
      : null;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-12"
      >
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 text-emerald-500 text-xs font-bold uppercase tracking-widest mb-6">
            <SealCheck size={16} weight="bold" /> Strategic Recommendation
            Finalized
          </div>
          <h2 className="text-4xl font-black text-white mb-4">
            Architecture Blueprint
          </h2>
          <p className="text-white/40 max-w-lg mx-auto leading-relaxed">
            Based on your organizational parameters, we recommend the following
            integrated health infrastructure.
          </p>
        </div>

        {/* Hero Package */}
        <div className="glass-panel p-1 rounded-[40px] overflow-hidden relative">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0048ff]/20 to-transparent pointer-events-none" />
          <div className="p-8 md:p-12 relative flex flex-col md:flex-row items-center gap-8 border-b border-white/10">
            <div className="w-24 h-24 rounded-3xl bg-white/5 border border-white/10 flex items-center justify-center text-[#0048ff] shadow-[0_0_30px_rgba(0,72,255,0.1)]">
              <PrimaryIcon size={48} weight="duotone" />
            </div>
            <div className="text-center md:text-left">
              <p className="text-[#0048ff] font-black uppercase tracking-[0.2em] text-[10px] mb-2">
                Core Component
              </p>
              <h3 className="text-3xl font-black text-white mb-4 tracking-tight">
                {result.primary_plan}
              </h3>
              <div className="flex flex-wrap justify-center md:justify-start gap-2">
                <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/60 text-xs font-medium">
                  Enterprise Scalable
                </span>
                <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/60 text-xs font-medium">
                  Auto-Governance
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/10 bg-white/[0.02]">
            <div className="p-8 space-y-4">
              <p className="text-[10px] font-black text-white/30 uppercase tracking-[0.2em]">
                Strategic Rationale
              </p>
              <p className="text-sm text-white/70 leading-relaxed italic">
                "{result.why_selected}"
              </p>
            </div>
            <div className="p-8 space-y-6">
              <p className="text-[10px] font-black text-white/30 uppercase tracking-[0.2em]">
                Integration Layer
              </p>
              <div className="space-y-4">
                {result.secondary_plan && (
                  <div className="flex items-center gap-4 group">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-500">
                      {SecondaryIcon && (
                        <SecondaryIcon size={20} weight="duotone" />
                      )}
                    </div>
                    <div>
                      <p className="text-xs font-bold text-white mb-0.5">
                        {result.secondary_plan}
                      </p>
                      <p className="text-[10px] text-emerald-500/70 font-semibold uppercase tracking-widest">
                        Premium Add-on
                      </p>
                    </div>
                  </div>
                )}
                {result.optional_plan && (
                  <div className="flex items-center gap-4 group">
                    <div className="w-10 h-10 rounded-xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center text-violet-500">
                      {OptionalIcon && (
                        <OptionalIcon size={20} weight="duotone" />
                      )}
                    </div>
                    <div>
                      <p className="text-xs font-bold text-white mb-0.5">
                        {result.optional_plan}
                      </p>
                      <p className="text-[10px] text-violet-500/70 font-semibold uppercase tracking-widest">
                        Expansion Rail
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="glass-panel p-8 space-y-6">
            <p className="text-[10px] font-black text-[#0048ff] uppercase tracking-[0.2em]">
              Competitive Advantage
            </p>
            <h4 className="text-xl font-black text-white">Efficiency Gains</h4>
            <ul className="space-y-4">
              {result.why_not_others &&
                result.why_not_others.map((reason, idx) => (
                  <li
                    key={idx}
                    className="flex gap-3 text-sm text-white/60 leading-relaxed"
                  >
                    <Lightning
                      size={18}
                      className="text-amber-500 flex-shrink-0"
                      weight="fill"
                    />
                    {reason}
                  </li>
                ))}
            </ul>
          </div>
          <div className="glass-panel p-8 space-y-6">
            <p className="text-[10px] font-black text-emerald-500 uppercase tracking-[0.2em]">
              Expected Outcomes
            </p>
            <h4 className="text-xl font-black text-white">Impact Analysis</h4>
            <div className="grid grid-cols-1 gap-3">
              {result.value_outcomes &&
                result.value_outcomes.map((outcome, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/10"
                  >
                    <CheckCircle
                      size={20}
                      className="text-emerald-500"
                      weight="bold"
                    />
                    <span className="text-xs font-bold text-white/80 tracking-wide">
                      {outcome}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* Mapping Section */}
        <div className="glass-panel p-8 md:p-12">
          <p className="text-[10px] font-black text-white/30 uppercase tracking-[0.2em] mb-8 text-center italic">
            Node Resolution: Bottleneck vs Solution
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {result.pain_point_mapping &&
              result.pain_point_mapping.map((item, idx) => (
                <div
                  key={idx}
                  className="p-6 rounded-2xl bg-white/5 border border-white/10 group hover:border-[#0048ff]/30 transition-all"
                >
                  <p className="text-sm font-bold text-white mb-4 group-hover:text-[#0048ff] transition-colors">
                    {item.pain_point}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {item.solutions.map((sol, sidx) => (
                      <span
                        key={sidx}
                        className="px-2 py-1 rounded bg-[#0048ff]/10 text-[#0048ff] text-[9px] font-black uppercase tracking-widest border border-[#0048ff]/20"
                      >
                        {sol}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Guidance Protocol */}
        <div className="glass-panel p-8 md:p-12 bg-[#0048ff]/5 border-[#0048ff]/20">
          <div className="text-center mb-10">
            <h4 className="text-2xl font-black text-white mb-2">
              Operational Guidelines
            </h4>
            <p className="text-white/40 text-sm">
              Strategic deployment pathways for different enterprise sizes.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="space-y-4">
              <h5 className="text-[#0048ff] font-black text-xs uppercase tracking-[0.2em]">
                Standard Rail
              </h5>
              <div className="space-y-2 text-white/60 text-xs font-medium leading-relaxed">
                <p>• Optimized for &lt;100 active lives</p>
                <p>• Fixed premium structure</p>
                <p>• Low administrative overhead</p>
                <p>• Rapid deployment protocol</p>
              </div>
            </div>
            <div className="space-y-4">
              <h5 className="text-emerald-400 font-black text-xs uppercase tracking-[0.2em]">
                Governance Pool
              </h5>
              <div className="space-y-2 text-white/60 text-xs font-medium leading-relaxed">
                <p>• Engineered for 500+ scale</p>
                <p>• Strategic liquidity control</p>
                <p>• Real-time claim monitoring</p>
                <p>• Advanced actuarial audits</p>
              </div>
            </div>
            <div className="space-y-4">
              <h5 className="text-violet-400 font-black text-xs uppercase tracking-[0.2em]">
                Hybrid Architecture
              </h5>
              <div className="space-y-2 text-white/60 text-xs font-medium leading-relaxed">
                <p>• Mixed insurance/wallet needs</p>
                <p>• Multi-node health access</p>
                <p>• Flexible funding mechanisms</p>
                <p>• Cross-border scalability</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Center */}
        <div className="flex flex-col items-center gap-10 py-12">
          <div className="flex flex-wrap justify-center gap-4">
            <Link to={result.primary_cta.href}>
              <Button className="h-16 px-10 rounded-2xl bg-[#0048ff] hover:bg-[#003bce] text-base font-black tracking-tight gap-4">
                {result.primary_cta.label} <ArrowRight weight="bold" />
              </Button>
            </Link>
            <Link to={result.secondary_cta.href}>
              <Button
                variant="outline"
                className="h-16 px-10 rounded-2xl border-white/10 bg-white/5 hover:bg-white/10 text-white font-black text-base tracking-tight"
              >
                {result.secondary_cta.label}
              </Button>
            </Link>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/register">
              <Button
                variant="ghost"
                className="text-white/40 hover:text-white font-bold text-xs uppercase tracking-widest gap-2"
              >
                <Plus size={16} /> Register Entity
              </Button>
            </Link>
            <div className="w-px h-10 bg-white/5 mx-2" />
            <Link to="/demo">
              <Button
                variant="ghost"
                className="text-white/40 hover:text-white font-bold text-xs uppercase tracking-widest gap-2"
              >
                <Compass size={16} /> Request Audit
              </Button>
            </Link>
            <div className="w-px h-10 bg-white/5 mx-2" />
            <Button
              variant="ghost"
              onClick={() => {
                setStep(1);
                setResult(null);
              }}
              className="text-[#0048ff] hover:text-[#0048ff]/80 font-bold text-xs uppercase tracking-widest gap-2"
            >
              <CircleNotch size={16} /> Reset Parameters
            </Button>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <Layout>
      <section className="min-h-screen bg-[#030712] relative overflow-hidden pt-24 pb-20">
        <LiquidGlassBg />

        <div className="container-wide relative z-10 px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Header Phase */}
            {step <= 9 && (
              <motion.div
                className="text-center mb-16"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="w-16 h-16 rounded-2xl bg-[#0048ff]/10 border border-[#0048ff]/20 flex items-center justify-center mx-auto mb-8 shadow-[0_0_30px_rgba(0,72,255,0.1)]">
                  <Compass
                    size={32}
                    className="text-[#0048ff]"
                    weight="duotone"
                  />
                </div>
                <h1 className="text-4xl md:text-5xl font-black text-white mb-6 uppercase tracking-tight">
                  Architectural Strategy
                </h1>
                <p className="text-white/40 max-w-lg mx-auto leading-relaxed font-medium">
                  Resolve your organizational healthcare complexities by
                  answering our core structural nodes.
                </p>
              </motion.div>
            )}

            {step <= 9 && renderProgressBar()}

            <div className={`relative transition-all duration-500`}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3, ease: "circOut" }}
                  className={`${step === 10 ? "" : "glass-panel p-8 md:p-12"}`}
                >
                  {renderStep()}

                  {error && (
                    <div className="mt-8 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 text-xs font-bold uppercase tracking-widest text-center">
                      <WarningCircle size={18} className="inline mr-2" />{" "}
                      {error}
                    </div>
                  )}

                  {step <= 9 && (
                    <div className="flex gap-4 mt-12">
                      {step > 1 && (
                        <Button
                          variant="outline"
                          onClick={prevStep}
                          className="h-14 flex-1 border-white/10 bg-white/5 hover:bg-white/10 text-white font-black uppercase tracking-widest text-xs"
                        >
                          <ArrowLeft size={16} className="mr-2" /> Previous Node
                        </Button>
                      )}
                      <Button
                        onClick={nextStep}
                        disabled={!canProceed() || loading}
                        className="h-14 flex-1 bg-[#0048ff] hover:bg-[#003bce] text-white font-black uppercase tracking-widest text-xs gap-3"
                      >
                        {loading
                          ? "Synthesizing..."
                          : step === 9
                            ? "Generate Blueprint"
                            : "Commit & Proceed"}
                        {!loading && <ArrowRight size={16} />}
                      </Button>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Footer Metrics */}
            {step <= 9 && (
              <div className="mt-20 pt-8 border-t border-white/5 text-center">
                <p className="text-[10px] font-black text-white/20 uppercase tracking-[0.3em]">
                  Infrastructure Analysis Protocol v3.0
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
